/**
 * Drag and Drop hook — manages card reorder within lists,
 * card move between lists/swimlanes, list reorder.
 * Uses optimistic updates with rollback on server failure.
 */

export interface DragItem {
  readonly id: string;
  readonly type: 'card' | 'list';
  readonly sourceListId?: string;
  readonly sourceSwimlaneId?: string;
  readonly sourceSort: number;
}

export interface DropTarget {
  readonly listId: string;
  readonly swimlaneId?: string;
  readonly sort: number;
}

export interface DragState {
  readonly isDragging: boolean;
  readonly dragItem: DragItem | null;
  readonly dropTarget: DropTarget | null;
  readonly invalidTarget: boolean;
}

export interface MoveCardRequest {
  readonly cardId: string;
  readonly targetListId: string;
  readonly targetSwimlaneId: string;
  readonly targetSort: number;
}

export interface ReorderListRequest {
  readonly listId: string;
  readonly targetSort: number;
}

export interface DragCallbacks {
  onMoveCard(request: MoveCardRequest): Promise<{ success: boolean }>;
  onReorderList(request: ReorderListRequest): Promise<{ success: boolean }>;
  checkWipLimit(listId: string): Promise<{ allowed: boolean; error?: string }>;
}

export interface DragAndDropResult {
  readonly state: DragState;
  readonly startDrag: (item: DragItem) => void;
  readonly updateTarget: (target: DropTarget | null) => void;
  readonly drop: () => Promise<DropResult>;
  readonly cancel: () => void;
}

export interface DropResult {
  readonly success: boolean;
  readonly error?: string;
  readonly reverted: boolean;
}

const INITIAL_STATE: DragState = {
  isDragging: false,
  dragItem: null,
  dropTarget: null,
  invalidTarget: false,
};

/**
 * Creates a drag-and-drop controller.
 * Stateless function — returns new state on each operation.
 * React component would wrap this with useState.
 */
export function createDragController(callbacks: DragCallbacks) {
  let state: DragState = { ...INITIAL_STATE };

  function startDrag(item: DragItem): DragState {
    state = { isDragging: true, dragItem: item, dropTarget: null, invalidTarget: false };
    return state;
  }

  async function updateTarget(target: DropTarget | null): Promise<DragState> {
    if (!state.dragItem || !target) {
      state = { ...state, dropTarget: target, invalidTarget: false };
      return state;
    }

    // Check WIP limit for card drops to a different list
    if (state.dragItem.type === 'card' && target.listId !== state.dragItem.sourceListId) {
      const wipCheck = await callbacks.checkWipLimit(target.listId);
      state = { ...state, dropTarget: target, invalidTarget: !wipCheck.allowed };
      return state;
    }

    state = { ...state, dropTarget: target, invalidTarget: false };
    return state;
  }

  async function drop(): Promise<DropResult> {
    if (!state.dragItem || !state.dropTarget) {
      state = { ...INITIAL_STATE };
      return { success: false, error: 'No drag item or target', reverted: false };
    }

    if (state.invalidTarget) {
      state = { ...INITIAL_STATE };
      return { success: false, error: 'Target has reached WIP limit', reverted: true };
    }

    const { dragItem, dropTarget } = state;

    if (dragItem.type === 'card') {
      const result = await callbacks.onMoveCard({
        cardId: dragItem.id,
        targetListId: dropTarget.listId,
        targetSwimlaneId: dropTarget.swimlaneId ?? '',
        targetSort: dropTarget.sort,
      });

      state = { ...INITIAL_STATE };

      if (!result.success) {
        return { success: false, error: 'Server rejected the move', reverted: true };
      }
      return { success: true, reverted: false };
    }

    if (dragItem.type === 'list') {
      const result = await callbacks.onReorderList({
        listId: dragItem.id,
        targetSort: dropTarget.sort,
      });

      state = { ...INITIAL_STATE };

      if (!result.success) {
        return { success: false, error: 'Server rejected the reorder', reverted: true };
      }
      return { success: true, reverted: false };
    }

    state = { ...INITIAL_STATE };
    return { success: false, error: 'Unknown drag type', reverted: false };
  }

  function cancel(): DragState {
    state = { ...INITIAL_STATE };
    return state;
  }

  function getState(): DragState {
    return state;
  }

  return { startDrag, updateTarget, drop, cancel, getState };
}
